/**
 * A single portfolio project.
 *
 * Declared here rather than inferred so the four entries below are checked
 * against one shape: adding a project and forgetting a field is a type error
 * instead of an `undefined` rendering on the page.
 */
export interface Project {
  /** Stable identifier, used as the React key. */
  id: string;
  /** Full project name. */
  title: string;
  /** Organisation the work was delivered for. */
  client: string;
  /** Short domain label, shown beside the client. */
  category: string;
  /** One or two sentences on what the system is. */
  description: string;
  /** The engineering focus: what was actually built. */
  contribution: string;
  /** Rendered as tags. Keep this a technology list, not a prose list. */
  technologies: string[];
  /**
   * At most one project sets this. The Projects page gives it the wider slot and
   * moves it to the top; nothing is hardcoded for a particular project.
   */
  featured?: boolean;
}

/**
 * The four professional engagements.
 *
 * Declared as its own annotated constant rather than inline in the siteConfig
 * literal, because an object-literal property cannot carry a type annotation:
 * `projects: Project[] = [...]` parses as the expression `Project[]`, i.e. an
 * element access with no argument. Annotating here means every entry below is
 * still checked against Project, so a new project missing a field is a type
 * error rather than an `undefined` on the page.
 *
 * This replaced three placeholder entries (a scheduling app, a routine tracker
 * and this portfolio) that the resume does not support and that described a
 * Node/MERN stack contradicting the documented .NET and Java background.
 *
 * Field notes:
 *   title/description  the original two fields, unchanged in meaning
 *   id                 stable React key; array position is not a safe key
 *   client/category    who the work was for, and the domain it sat in
 *   contribution       what was actually built. Integrations (Device SDK, SAP)
 *                      live here as prose so `technologies` stays a tech list
 *   featured           at most one project; the page hoists it to the top and
 *                      gives it the wider slot. No project is special-cased in
 *                      the markup.
 */
const projects: Project[] = [
  {
    id: 'smart-iot-platform',
    title: 'Smart IoT Home & Appliance Platform',
    client: 'Tata Power',
    category: 'IoT & Connected Devices',
    description:
      'Backend services for a connected-appliance platform, covering device registration, telemetry ingestion and the control surface the consumer app calls.',
    contribution:
      'Built the device-facing API layer and integrated the vendor Device SDKs so appliance telemetry could be received and stored, using Dapper for lean data access against PostgreSQL.',
    technologies: ['.NET Core', 'PostgreSQL', 'Dapper', 'REST APIs', 'AWS'],
  },
  {
    id: 'itot-asset-isms-platform',
    title: 'IT/OT Asset Management & ISMS Platform',
    client: 'Deloitte India',
    category: 'Asset & Compliance',
    description:
      'Asset lifecycle management for an enterprise IT/OT estate, paired with an integrated safety management system covering risk tracking and compliance evidence.',
    contribution:
      'Delivered the asset and ISMS modules with background job processing for the recurring compliance and reporting work, and integrated SAP for asset data exchange.',
    technologies: ['.NET Core', 'PostgreSQL', 'REST APIs', 'Background Jobs', 'SAP', 'GCP', 'Windows Server'],
  },
  {
    id: 'energy-data-collection-platform',
    title: 'Energy & Resources Data Collection Platform',
    client: 'SMRD Tata Power',
    category: 'Energy & Resources',
    description:
      'Field data collection for energy and resources operations, covering resource records, the collection workflow and the reporting built on top of it.',
    contribution:
      'Implemented the backend services and REST endpoints behind the collection workflow, persisting operational data in PostgreSQL.',
    technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'REST APIs'],
  },
  {
    id: 'aviation-operations-platform',
    title: 'Aviation Trip & Operations Management Platform',
    client: 'Universal Weather and Aviation',
    category: 'Aviation Operations',
    description:
      'Trip and operations management for an aviation platform, coordinating itinerary data, operational scheduling and the event flow that keeps downstream systems in step.',
    contribution:
      'Built the service layer end to end — Clean Architecture with CQRS and the Repository pattern over Oracle, SQL Server and MongoDB, asynchronous processing on Azure Service Bus, and the React/TypeScript front end.',
    technologies: [
      'C#',
      'ASP.NET Core',
      'React',
      'TypeScript',
      'Clean Architecture',
      'CQRS',
      'Repository Pattern',
      'Azure Service Bus',
      'Background Jobs',
      'Oracle',
      'SQL Server',
      'MongoDB',
    ],
    featured: true,
  },
];

/**
 * Which icon leads a card.
 *
 * A name rather than a component, so this file stays pure data and imports no
 * icon library. The names are resolved to actual icons in
 * components/extensions/icons.ts, and that map is typed against this union — so
 * a name added here without a matching icon is a compile error rather than a
 * card that silently loses its glyph.
 */
export type ExtensionIcon = 'fold' | 'regex' | 'terminal' | 'blocks' | 'puzzle';

/**
 * Which inline demo an entry plays in its card.
 *
 * Same split as ExtensionIcon: the config names the demo, and
 * components/extensions/demos.ts resolves the name to a component. An entry with
 * no demo renders no demo, which is the right default — a demo is a claim that
 * the page can actually show the extension working, and only some extensions are
 * worth that.
 */
export type ExtensionDemo = 'fold' | 'regex';

/**
 * One outbound destination for an extension.
 *
 * A list rather than fixed `marketplaceUrl` / `githubUrl` / `demoUrl` fields, so
 * an extension can link to whatever it actually has — marketplace, repository,
 * demo, documentation — without the shape of the config having to change first.
 */
export interface ExtensionLink {
  /** Visible text on the link, e.g. "Marketplace". */
  label: string;
  /** Absolute URL. */
  href: string;
  /**
   * Opened in a new tab, with `rel="noopener noreferrer"`. Defaults to true
   * because every link on this page points at another site; set it to false
   * only for a destination that genuinely belongs in the same tab.
   */
  external?: boolean;
}

/**
 * A single published editor extension.
 *
 * Modelled on `Project` above: declared here so every entry is checked against
 * one shape, and so adding an extension is a data change rather than new JSX.
 */
export interface Extension {
  /** Stable identifier, used as the React key. */
  id: string;
  /** Full extension name, as listed in the marketplace. */
  title: string;
  /** What it is, e.g. "VS Code Extension". */
  platform: string;
  /** The domain it sits in, e.g. "Developer Tool". */
  category: string;
  /** The problem it addresses, in one or two sentences. */
  description: string;
  /** What it actually does. Kept short: this is an overview, not the README. */
  features: string[];
  /** Rendered as tags. Keep this a technology list, not a prose list. */
  technologies: string[];
  /** Outbound destinations. Entries are rendered in order, and an empty list
   *  renders no link row at all rather than an empty button. */
  links: ExtensionLink[];
  /** Leading icon. Optional: an entry without one falls back to a neutral
   *  extension glyph, so a new listing is never blocked on picking an icon. */
  icon?: ExtensionIcon;
  /** Optional inline demo, played in the card. Omitted entries render none, so
   *  an extension whose behaviour is not worth animating stays text-only. */
  demo?: ExtensionDemo;
  /** Shown as the trailing meta item, e.g. a licence. Optional. */
  license?: string;
  /** Copy-and-run install line, e.g. the `code --install-extension` form.
   *  Optional: omitted for anything not published to a registry. */
  installCommand?: string;
  /** Optional preview image. Lazy-loaded, and omitted entirely when unset, so
   *  an extension without media costs nothing. */
  demoImage?: string;
  /** Required whenever `demoImage` is set — a decorative image would be worse
   *  than no image, since the demo is the only thing the image conveys. */
  demoImageAlt?: string;
}

/**
 * The published extensions.
 *
 * Field notes:
 *   id                  stable React key; array position is not a safe key
 *   platform / category the meta row above the title, both from data so the
 *                       card never assumes every entry is a VS Code extension
 *   features            the functional summary; the marketplace listing and the
 *                       repository carry the full documentation
 *   links               ordered, and conditional — see `ExtensionLink`
 *   installCommand      only for a published marketplace entry
 *   demoImage           lazy, and absent for entries with no recording
 */
const extensions: Extension[] = [
  {
    id: 'styled-component-folder',
    title: 'Styled Component Folder',
    platform: 'VS Code Extension',
    category: 'Developer Tool',
    description:
      'Folds and unfolds every styled-components block in the current file, so a long file of styled declarations can be read at a glance instead of scrolled through.',
    features: [
      'Folds all styled-components in the current file with one command',
      'Unfolds all styled-components in the current file',
      'Bound to Ctrl + Alt + F for folding by default',
      'Recognises styled declarations in .ts, .tsx, .js and .jsx files',
    ],
    technologies: ['TypeScript', 'VS Code API'],
    license: 'MIT',
    icon: 'fold',
    // Folding styled-components blocks is the extension's whole reason to
    // exist, and it is the one thing here a static screenshot cannot convey.
    demo: 'fold',
    installCommand: 'code --install-extension PiyushSingh.styled-component-folder',
    links: [
      {
        label: 'Marketplace',
        href: 'https://marketplace.visualstudio.com/items?itemName=PiyushSingh.styled-component-folder',
      },
      {
        label: 'Repository',
        href: 'https://github.com/piyush-singh/styled-component-folder',
      },
      {
        label: 'Demo',
        href: 'https://github.com/piyush-singh/styled-component-folder',
      },
    ],
  },
  {
    id: 'regex-explainer',
    title: 'Regex Explainer',
    platform: 'VS Code Extension',
    category: 'Developer Tool',
    description:
      'Explains a regular expression when the pointer rests on it, so a pattern can be read without leaving the file or opening a separate tool.',
    features: [
      'Explains a pattern from a hover, with no panel to open and keep in sync',
      'Detects patterns written as literals, strings and RegExp constructors',
      'Breaks a pattern down token by token, including its flags',
      'Renders the explanation as Markdown in the native hover',
    ],
    technologies: ['TypeScript', 'VS Code API', 'Regex'],
    license: 'MIT',
    icon: 'regex',
    // The hover explanation is what the extension is, and a screenshot of a
    // tooltip is a screenshot of a tooltip: the token-by-token walk is the only
    // way to show what it actually adds.
    demo: 'regex',
    // No marketplace id and no demo recording supplied yet, so neither is
    // listed. Both render only if present, which is why this entry is shorter.
    links: [
      {
        label: 'Repository',
        href: 'https://github.com/piyush-singh/regex-explainer',
      },
    ],
  },
];

export const siteConfig = {
  name: 'Piyush Singh',
  headline: 'Software Developer',
  tagline: 'Building scalable Saas platforms and dependable web software with clear code, measured decisions, and focus on delivery.',
  tagline2: 'Helping startups and business launch production ready software, and grow their ideas.',
  roles: ['Software Developer', 'Full-stack Development', 'API-first .NET', 'Performance-focused, Responsive UI', 'Azure Cloud Solutions', 'Java Spring Boot', 'MERN Stack'],
  techStack: [
    'React + TypeScript + .NET',
    'APIs, internal tools, dashboards',
    'Performance, accessibility, tests'
  ],
  resume: {
    url: '/assets/resume.pdf',
    summary:
      'Full-stack developer focused on reliable web software, API-first systems, and pragmatic delivery.',
    experience: [
      { role: 'Software Developer', company: 'Freelance / Projects', period: '2023 — Present' },
      { role: 'Intern Developer', company: 'TimeCraft', period: '2022 — 2023' }
    ],
    education: [
      { degree: 'B.Tech in Computer Science', institution: 'University', period: '2019 — 2023' }
    ]
  },
  projects,
  extensions,

  socialLinks: {
    github: 'https://github.com/piyushsingh6521',
    linkedin: 'https://www.linkedin.com/in/piyush-singh-721977258/',
    twitter: 'https://twitter.com/',
    email: 'mailto:piyushsingh6521@gmail.com',
    phone: 'tel:+918382818030'
  }
};
